import React, { useEffect, useState } from "react";
import { Button, Form, InputGroup, Modal, Spinner } from "react-bootstrap";
import { MapContainer, Marker, TileLayer, useMapEvents } from "react-leaflet";
import { toast } from "react-toastify";
import { pickupIcon } from "../components/tripMap/TripMap";
import { dayDate, formatClock } from "../components/tripMap/tripMapModel";
import { locatePickupLink, saveCustomerPickup } from "../services/tripTrackingApi";

// Where the chooser opens with nothing to start from: Bengaluru, where most trips begin.
const FALLBACK_CENTER = [12.9716, 77.5946];

/**
 * The customer picks the exact pickup spot - their current location, a pasted Google Maps link, or
 * a tap on the map - plus a landmark, a number to call and the time. The time can move later on the
 * pickup day, never earlier than the booked time.
 */
export default function TrackingPickupModal({ show, token, tracking, onHide, onSaved }) {
  const [map, setMap] = useState(null);
  const [point, setPoint] = useState(null);
  const [linkSource, setLinkSource] = useState(null);
  const [link, setLink] = useState("");
  const [landmark, setLandmark] = useState("");
  const [phone, setPhone] = useState("");
  const [time, setTime] = useState("");
  const [locating, setLocating] = useState(false);
  const [findingLink, setFindingLink] = useState(false);
  const [saving, setSaving] = useState(false);

  const saved = tracking?.pickupPoint;
  const earliest = tracking?.pickupEarliestDateTime || null;
  const minTime = clockValue(earliest);
  const day = dayDate(earliest || tracking?.vehicleStartDate || tracking?.travelDate, 0);

  useEffect(() => {
    if (!show) return;
    setPoint(saved ? { lat: saved.latitude, lng: saved.longitude } : null);
    setLinkSource(null);
    setLink("");
    setLandmark(saved?.landmark || "");
    setPhone(saved?.contactPhone || tracking?.customerPhone || "");
    setTime(clockValue(tracking?.vehiclePickupDateTime) || minTime);
    // Only when the window opens: a background refresh must not wipe what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const start = saved
    ? { center: [saved.latitude, saved.longitude], zoom: 17 }
    : tracking?.pickupLatitude != null && tracking?.pickupLongitude != null
      ? { center: [tracking.pickupLatitude, tracking.pickupLongitude], zoom: 14 }
      : { center: FALLBACK_CENTER, zoom: 11 };

  const place = (next, focus) => {
    setPoint(next);
    if (focus && map) map.setView([next.lat, next.lng], 17);
  };
  const movePin = (latlng) => {
    setLinkSource(null);
    place({ lat: latlng.lat, lng: latlng.lng }, false);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Location is not available here. Please move the pin on the map.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        setLinkSource(null);
        place({ lat: position.coords.latitude, lng: position.coords.longitude }, true);
      },
      () => {
        setLocating(false);
        toast.error("Could not get your location. Allow location access, or move the pin on the map.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 },
    );
  };

  const findLink = async () => {
    const value = link.trim();
    if (!value) return;
    setFindingLink(true);
    try {
      const found = await locatePickupLink(token, value);
      const next = { lat: found.latitude, lng: found.longitude };
      place(next, true);
      setLinkSource({ link: value, ...next });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not read this link. Please move the pin on the map instead.");
    } finally {
      setFindingLink(false);
    }
  };

  const save = async () => {
    const problem = !point ? "Choose your pickup point on the map"
      : !landmark.trim() ? "Add a landmark or house details for the driver"
        : !phone.trim() ? "Enter a phone number for the driver to call at pickup"
          : !time ? "Choose your pickup time"
            : minTime && time < minTime ? `Pickup can't be earlier than your booked time, ${formatClock(earliest)}`
              : "";
    if (problem) {
      toast.error(problem);
      return;
    }
    setSaving(true);
    try {
      const fromLink = linkSource && linkSource.lat === point.lat && linkSource.lng === point.lng ? linkSource.link : null;
      await saveCustomerPickup(token, {
        latitude: point.lat,
        longitude: point.lng,
        link: fromLink,
        landmark: landmark.trim(),
        contactPhone: phone.trim(),
        pickupTime: time,
      });
      toast.success("Pickup point saved. Your driver will see it.");
      onSaved?.();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save the pickup point. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered fullscreen="sm-down" onEntered={() => map?.invalidateSize()}>
      <Modal.Header closeButton>
        <Modal.Title className="h5">
          <i className="bi bi-geo-alt-fill me-2" aria-hidden="true" style={{ color: "#047857" }} />
          Your pickup point
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="trip-pickup-body">
        <p className="trip-pickup-lead">Your driver comes to this exact spot. Choose it in any one way:</p>
        <Button variant="outline-success" className="w-100 mb-2" onClick={useMyLocation} disabled={locating}>
          {locating ? <Spinner size="sm" className="me-2" /> : <i className="bi bi-crosshair me-2" aria-hidden="true" />}
          Use my current location
        </Button>
        <InputGroup className="mb-2">
          <Form.Control
            placeholder="Or paste a Google Maps link"
            value={link}
            onChange={(event) => setLink(event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter") findLink(); }}
            aria-label="Google Maps link"
          />
          <Button variant="outline-secondary" onClick={findLink} disabled={findingLink || !link.trim()}>
            {findingLink ? <Spinner size="sm" /> : "Find"}
          </Button>
        </InputGroup>
        <div className="trip-pickup-map">
          <MapContainer ref={setMap} center={start.center} zoom={start.zoom} style={{ height: "100%" }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <TapToPlace onPlace={movePin} />
            {point ? (
              <Marker
                position={[point.lat, point.lng]}
                icon={pickupIcon()}
                draggable
                eventHandlers={{ dragend: (event) => movePin(event.target.getLatLng()) }}
              />
            ) : null}
          </MapContainer>
          <div className="trip-pickup-hint">
            {point ? "Drag the pin to the exact spot if needed" : "Or tap the map at your pickup spot"}
          </div>
        </div>

        <Form.Group className="mt-3">
          <Form.Label className="trip-pickup-label">Landmark / house details for the driver</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            maxLength={300}
            placeholder="e.g. Gate 2, Green Glen Apartments, opposite Shell petrol bunk"
            value={landmark}
            onChange={(event) => setLandmark(event.target.value)}
          />
        </Form.Group>
        <Form.Group className="mt-3">
          <Form.Label className="trip-pickup-label">Phone number to call at pickup</Form.Label>
          <Form.Control type="tel" inputMode="tel" value={phone} onChange={(event) => setPhone(event.target.value)} />
        </Form.Group>
        <Form.Group className="mt-3">
          <Form.Label className="trip-pickup-label">Pickup time{day ? ` · ${day}` : ""}</Form.Label>
          <Form.Control type="time" value={time} min={minTime || undefined} onChange={(event) => setTime(event.target.value)} />
          {earliest ? (
            <Form.Text className="trip-pickup-help">
              Your booked time is {formatClock(earliest)}. You can choose a later time on the same day, not an earlier one.
            </Form.Text>
          ) : null}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>Cancel</Button>
        <Button variant="success" onClick={save} disabled={saving}>
          {saving ? <Spinner size="sm" className="me-2" /> : null}
          Save pickup
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function TapToPlace({ onPlace }) {
  useMapEvents({ click: (event) => onPlace(event.latlng) });
  return null;
}

// "06:30" from "2026-11-02T06:30:00", the value a time box takes.
function clockValue(value) {
  const match = String(value || "").match(/T(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : "";
}
